import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Wrapper } from 'components'
import { AuthContext } from 'contexts/AuthContext'
import {
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
} from 'keep-react'
import { ArrowLeft } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { claimsService } from 'api/services/claims'
import {
  GetClaimClaimantResponse,
  GetClaimDetailCoverages,
  GetClaimDetailResponse,
  GetClaimHandlerResponse,
  GetClaimHolderResponse,
  GetClaimPaymentsResponse,
} from 'api/types/claims'
import { formatCurrency } from 'utils/currency'
import { formatDate } from 'utils/dates'
import { renderClaimsBadge } from 'utils/renderClaimBadge'

export const Claim = () => {
  const { claimNo } = useParams() as unknown as { claimNo: number }
  const [claim, setClaim] = useState<GetClaimHandlerResponse | null>(null)
  const [claimHolder, setClaimHolder] = useState<GetClaimHolderResponse | null>(
    null,
  )
  const [claimDetails, setClaimDetails] =
    useState<GetClaimDetailResponse | null>(null)
  const [claimClaimant, setClaimClaimant] =
    useState<GetClaimClaimantResponse | null>(null)
  const [claimPayments, setClaimPayments] =
    useState<GetClaimPaymentsResponse | null>(null)
  const [activeClaimantTab, setActiveClaimantTab] = useState<string>('')

  const navigate = useNavigate()
  const { currentBroker } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0

  //get policy details
  useEffect(() => {
    const fetchClaim = async () => {
      if (!claimNo || brokerNo === 0) return false

      try {
        const response = (await claimsService.getClaimHandler(
          Number(claimNo),
          brokerNo,
        )) as GetClaimHandlerResponse

        setClaim(response)
      } catch (error) {
        console.log('Error fetching claim details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    const fetchClaimHolder = async () => {
      if (!claimNo || brokerNo === 0) return false

      try {
        const response = (await claimsService.getClaimHolder(
          Number(claimNo),
          brokerNo,
        )) as GetClaimHolderResponse

        setClaimHolder(response)
      } catch (error) {
        console.log('Error fetching claim holder details:', error)
      }
    }

    const fetchClaimDetails = async () => {
      if (!claimNo || brokerNo === 0) return false

      try {
        const response = (await claimsService.getClaimDetail(
          Number(claimNo),
          brokerNo,
        )) as GetClaimDetailResponse

        setClaimDetails(response)
      } catch (error) {
        console.log('Error fetching claim details:', error)
      }
    }

    const fetchClaimClaimant = async () => {
      if (!claimNo || brokerNo === 0) return false

      try {
        const response = (await claimsService.getClaimClaimant(
          Number(claimNo),
          brokerNo,
        )) as GetClaimClaimantResponse

        setClaimClaimant(response)
      } catch (error) {
        console.log('Error fetching claim claimant details:', error)
      }
    }

    const fetchClaimPayments = async () => {
      if (!claimNo || brokerNo === 0) return false

      try {
        const response = (await claimsService.getClaimPayments(
          Number(claimNo),
          brokerNo,
        )) as GetClaimPaymentsResponse

        setClaimPayments(response)
      } catch (error) {
        console.log('Error fetching claim payments details:', error)
      }
    }

    fetchClaim()
    fetchClaimHolder()
    fetchClaimDetails()
    fetchClaimClaimant()
    fetchClaimPayments()
  }, [claimNo, brokerNo])

  useEffect(() => {
    const claimants = claimClaimant?.Claimants

    if (!claimants?.length) {
      setActiveClaimantTab('')
      return
    }

    const hasActiveClaimant = claimants.some(
      (_, index) => `claimant_${index}` === activeClaimantTab,
    )

    if (!hasActiveClaimant) {
      setActiveClaimantTab('claimant_0')
    }
  }, [activeClaimantTab, claimClaimant])

  if (!claim) {
    return <Spinner />
  }

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <Button
          variant="outline"
          color="secondary"
          className="mb-6"
          onClick={() => navigate(RouteNamesAuth.CLAIMS)}
        >
          <ArrowLeft size={16} /> Sinistros
        </Button>
        <div className="flex items-start gap-2 w-full">
          <h2 className="text-body-1 uppercase pb-10">
            Sinistro {claimNo || 'Sinistro indisponível'}
          </h2>
          <p>{renderClaimsBadge(claimDetails?.ClaimStatus || 'PENDING ')}</p>
        </div>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !claim ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Sinistro indisponível
            </div>
          </Card>
        ) : (
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 pb-8">
              <Card className="p-4 w-full max-w-full bg-metal-25">
                <div className="flex flex-col items-start gap-2 w-full ">
                  <h2
                    className={`text-body-1 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-4 font-bold`}
                  >
                    Dados do Tomador
                  </h2>
                  <Table className="bg-metal-25">
                    <TableBody>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold whitespace-nowrap">
                          Nº Apólice
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimHolder?.PolicyNo || 'Nº Apólice indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          NIF
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimHolder?.NIF || 'NIF indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Tomador
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimHolder?.Name || 'Tomador indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Morada
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimHolder?.Address || 'Morada indisponível'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </Card>

              <Card className="p-4 w-full max-w-full bg-metal-25">
                <div className="flex flex-col items-start gap-2 w-full">
                  <h2
                    className={`text-body-1 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-4 font-bold`}
                  >
                    Gestor do Sinistro
                  </h2>
                  <Table className="bg-metal-25">
                    <TableBody>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Nome
                        </TableHead>
                        <TableCell className="normal-case">
                          {claim.Name || 'Nome indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Telefone
                        </TableHead>
                        <TableCell className="normal-case">
                          {claim.Mobile || 'Telefone indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Email
                        </TableHead>
                        <TableCell className="normal-case">
                          {claim.Email || 'Email indisponível'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </Card>

              <Card className="p-4 w-full max-w-full bg-metal-25">
                <div className="flex flex-col items-start gap-2 w-full">
                  <h2
                    className={`text-body-1 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-4 font-bold`}
                  >
                    Dados do Sinistro
                  </h2>
                  <Table className="pb-10 bg-metal-25">
                    <TableBody>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Data Ocorrência
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimDetails?.IncidentDate
                            ? formatDate(claimDetails?.IncidentDate).dashed
                            : 'Data Ocorrência indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Decisão
                        </TableHead>
                        <TableCell className="normal-case"></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Local
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimDetails?.IncidentAddress ||
                            'Local indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Peritagem
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimDetails?.Inspection || 'Peritagem indisponível'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHead className="!h-10 bg-metal-25 font-bold">
                          Coberturas
                        </TableHead>
                        <TableCell className="normal-case">
                          {claimDetails?.Coverages.map(
                            (
                              coverage: GetClaimDetailCoverages,
                              index: number,
                            ) => (
                              <div key={`cobertura_${index}`}>
                                {coverage.Description}
                              </div>
                            ),
                          ) || 'Coberturas indisponível'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </Card>

              <Card className="p-4 w-full max-w-full bg-metal-25">
                <div className="flex flex-col items-start gap-2 w-full">
                  <h2
                    className={`text-body-1 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-4 font-bold`}
                  >
                    Descrição do Sinistro
                  </h2>
                  <p>
                    {claimDetails?.IncidentDescription ||
                      'Descrição do Sinistro indisponível'}
                  </p>
                </div>
              </Card>
            </div>

            <h2
              className={`text-body-1 text-white ${currentBroker?.IsPrimePartner ? 'bg-prime' : 'bg-primary-600'} p-4 font-bold`}
            >
              Dados do Lesado
            </h2>
            <div className="pb-8">
              <div>
                <Card className="p-4 w-full max-w-full bg-metal-25">
                  <div className="flex flex-col items-start gap-2 w-full">
                    {claimClaimant?.Claimants?.length ? (
                      <Tabs
                        value={activeClaimantTab}
                        onValueChange={setActiveClaimantTab}
                      >
                        <TabsList>
                          {claimClaimant.Claimants.map((claimant, index) => (
                            <TabsItem
                              key={`claimant_${index}`}
                              value={`claimant_${index}`}
                            >
                              {claimant.Name}
                            </TabsItem>
                          ))}
                        </TabsList>

                        {claimClaimant.Claimants.map((claimant, index) => (
                          <TabsContent
                            key={`claimant_content_${index}`}
                            value={`claimant_${index}`}
                          >
                            <Table className="pb-10 bg-metal-25">
                              <TableBody>
                                <TableRow>
                                  <TableHead className="!h-10 bg-metal-25 font-bold">
                                    Nome
                                  </TableHead>
                                  <TableCell className="normal-case">
                                    {claimant.Name || 'Nome indisponível'}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableHead className="!h-10 bg-metal-25 font-bold">
                                    Telefone
                                  </TableHead>
                                  <TableCell className="normal-case">
                                    {claimant.Phone || 'Telefone indisponível'}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableHead className="!h-10 bg-metal-25 font-bold">
                                    Morada
                                  </TableHead>
                                  <TableCell className="normal-case">
                                    {claimant.Address || 'Morada indisponível'}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableHead className="!h-10 bg-metal-25 font-bold">
                                    Email
                                  </TableHead>
                                  <TableCell className="normal-case">
                                    {claimant.Email || 'Email indisponível'}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableHead className="!h-10 bg-metal-25 font-bold">
                                    Coberturas
                                  </TableHead>
                                  <TableCell className="normal-case">
                                    {claimDetails?.Coverages.map(
                                      (
                                        coverage: GetClaimDetailCoverages,
                                        index: number,
                                      ) => (
                                        <div key={`cobertura_${index}`}>
                                          {coverage.Description}
                                        </div>
                                      ),
                                    ) || 'Coberturas indisponível'}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TabsContent>
                        ))}
                      </Tabs>
                    ) : (
                      <p>Dados do lesado indisponiveis</p>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            <h2
              className={`text-body-1 text-white ${currentBroker?.IsPrimePartner ? 'bg-prime' : 'bg-primary-600'} p-4 font-bold w-full`}
            >
              Pagamentos realizados
            </h2>
            <div className="pb-8">
              <Card className="p-4 w-full max-w-full bg-metal-25">
                <div className="flex flex-col items-start gap-2 w-full">
                  {claimPayments?.Payments?.length ? (
                    <Table className="pb-10 bg-metal-25">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="!h-10 bg-metal-25 font-bold">
                            Cobertura
                          </TableHead>
                          <TableHead className="!h-10 bg-metal-25 font-bold">
                            Montante
                          </TableHead>
                          <TableHead className="!h-10 bg-metal-25 font-bold">
                            Estado
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {claimPayments.Payments.map((payment, index) => (
                          <TableRow key={`payment_${index}`}>
                            <TableCell className="normal-case">
                              {payment.Coverage || 'Cobertura indisponível'}
                            </TableCell>
                            <TableCell className="normal-case">
                              {formatCurrency(payment.Amount) ||
                                'Montante indisponível'}
                            </TableCell>
                            <TableCell className="normal-case">
                              {payment.State || 'Estado indisponível'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p>Pagamentos indisponíveis</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </Wrapper>
    </div>
  )
}
